import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import VerifiedBadge from "../components/VerifiedBadge";
import { MapPin, Briefcase, Wifi, Building2, ArrowLeft, Send, Trash2 } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [coverLetter, setCoverLetter] = useState("");
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState({});

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => toast.error("Offre introuvable"));
    api.get("/countries").then((r) => setCountries(Object.fromEntries(r.data.map((c) => [c.code, c]))));
  }, [id]);

  const apply = async () => {
    if (!user) { nav("/login"); return; }
    setSubmitting(true);
    try {
      await api.post("/applications", { job_id: id, cover_letter: coverLetter });
      toast.success("Candidature envoyée !");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const deleteJob = async () => {
    if (!window.confirm("Supprimer cette offre ?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Offre supprimée");
    nav("/dashboard");
  };

  if (!job) return <div className="max-w-3xl mx-auto p-10 text-slate-500">Chargement...</div>;

  const owner = user && user.id === job.employer_id;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="job-detail-page">
      <Link to="/jobs" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-jobs">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux offres
      </Link>

      <div className="mec-card p-8">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="font-mono text-[10px] tracking-widest uppercase">{job.contract_type}</Badge>
            {job.remote && <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200"><Wifi className="w-3 h-3 mr-1" />Remote</Badge>}
            {job.sector && <Badge variant="outline">{job.sector}</Badge>}
          </div>
          {owner && (
            <Button variant="outline" size="sm" onClick={deleteJob} data-testid="delete-job">
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
            </Button>
          )}
        </div>

        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-3">{job.title}</h1>
        <Link to={`/employers/${job.employer_id}`} className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm mb-6">
          <Building2 className="w-4 h-4" /> {job.employer_name}
          <VerifiedBadge status={job.employer_verification_status} size="md" />
        </Link>

        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600 mb-6 pb-6 border-b border-slate-200">
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" />{countries[job.country_code]?.flag} {countries[job.country_code]?.name}{job.city ? `, ${job.city}` : ""}</span>
          {(job.salary_min || job.salary_max) && (
            <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4" />{job.salary_min}-{job.salary_max} {job.currency}</span>
          )}
        </div>

        <div className="prose-mec mb-6 whitespace-pre-line text-slate-700">{job.description}</div>

        {job.skills_required?.length > 0 && (
          <div className="mb-6">
            <div className="mec-overline mb-2">Compétences</div>
            <div className="flex flex-wrap gap-2">
              {job.skills_required.map((s, i) => (
                <Badge key={i} variant="outline" className="border-slate-300">{s}</Badge>
              ))}
            </div>
          </div>
        )}

        {!owner && user?.role === "candidate" && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="bg-[#004EEB] hover:bg-[#003BCC] text-white" data-testid="apply-btn">
                <Send className="w-4 h-4 mr-2" /> Postuler à cette offre
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Postuler — {job.title}</DialogTitle>
              </DialogHeader>
              <div className="py-2">
                <label className="text-sm font-medium text-slate-700">Lettre de motivation (optionnelle)</label>
                <Textarea rows={8} value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} className="mt-1.5" data-testid="cover-letter" />
                <p className="text-xs text-slate-500 mt-2">
                  Astuce : utilisez l'<Link to="/ai-assistant" className="text-[#004EEB] underline">Assistant IA</Link> pour améliorer votre lettre.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={apply} disabled={submitting} className="bg-[#004EEB] hover:bg-[#003BCC]" data-testid="submit-apply">
                  {submitting ? "Envoi..." : "Envoyer ma candidature"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        {!user && (
          <Link to="/login"><Button className="bg-[#004EEB] hover:bg-[#003BCC] text-white">Se connecter pour postuler</Button></Link>
        )}
      </div>
    </div>
  );
}
